import { useEditor, useNode } from "@craftjs/core";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useSelector } from "react-redux";

import PrimitivesFloatingOptions from "@/components/editor/block-outline/floating-options/primitives-floating-component";
import { getRecordingById } from "@/content-hub/store/selectors";
import {
  EmbedVideoCard,
  RecordingVideoCard,
} from "@/content-hub/theme/primitives/video-cards";
import { inferYoutubePosterFromEmbedUrl } from "@/content-hub/utils/resolve-video-block-poster-for-details";
import type { RootState } from "@/redux/store";

import { hasDisplayDescriptionContent } from "./has-display-description-content";
import {
  effectiveRecordingDisplay,
  resolveVideoDescriptionHtml,
} from "./recording-metadata-merge";
import { VideoSettings } from "./settings";
import { VideoContainer } from "./video-styles";

type VideoContent = {
  url?: string;
  thumbnail?: string;
  title?: string;
  recordingId?: string;
  displayTitle?: string;
  displayDescription?: string;
  displayThumbnail?: string;
};

export type VideoBlockProps = {
  isMuted?: boolean;
} & VideoContent;

export default function Video(props: VideoBlockProps) {
  const {
    recordingId,
    url,
    thumbnail,
    title,
    isMuted,
    displayTitle,
    displayDescription,
    displayThumbnail,
  } = props;
  const {
    id,
    connectors: { connect },
  } = useNode();

  const { isEditorEnabled } = useEditor((state) => ({
    isEditorEnabled: state.isEnabled(),
  }));

  const recording = useSelector((state: RootState) =>
    recordingId ? getRecordingById(recordingId)(state) : undefined
  );
  const flags = useFlags();
  const singleVideoSettingsV2 = Boolean(flags?.contentHubSingleVideoSettingsV2);
  const directVideoSharingEnabled = Boolean(flags.contentHubDirectVideoSharing);
  // Legacy single-video only when neither v2 settings nor direct sharing is on.
  const suppressLegacyDirectVideoSharing =
    !singleVideoSettingsV2 && !directVideoSharingEnabled;

  const displayMetadata = {
    displayTitle,
    displayDescription,
    displayThumbnail,
  };

  const mergedRecordingDisplay =
    recording && effectiveRecordingDisplay(recording, displayMetadata);

  const embedDisplayTitle = (displayTitle ?? title) || "";
  const customEmbedThumb = displayThumbnail?.trim();
  const displayEmbedThumbnail =
    customEmbedThumb ||
    thumbnail ||
    inferYoutubePosterFromEmbedUrl(url ?? "") ||
    undefined;

  const descriptionHtml = resolveVideoDescriptionHtml(
    recording,
    displayMetadata
  );

  const overlayDescriptionHtml =
    directVideoSharingEnabled &&
    !isEditorEnabled &&
    hasDisplayDescriptionContent(descriptionHtml)
      ? descriptionHtml
      : undefined;

  return (
    <VideoContainer
      ref={connect}
      id={id}
      data-section-id={id}
      data-testid="video-component"
    >
      {recordingId && recording && mergedRecordingDisplay ? (
        <RecordingVideoCard
          key={recording.id}
          showCaption={true}
          recording={recording}
          aspectRatio={"wide"}
          videoId={directVideoSharingEnabled ? recording.id : undefined}
          shareSectionId={directVideoSharingEnabled ? id : undefined}
          displayTitle={mergedRecordingDisplay.title}
          displayThumbnailUrl={mergedRecordingDisplay.thumbnail}
          overlayDescriptionHtml={overlayDescriptionHtml}
          suppressDirectVideoSharingFeatures={suppressLegacyDirectVideoSharing}
          playerOptions={{
            muted: isMuted,
          }}
        />
      ) : (
        <EmbedVideoCard
          url={url ?? ""}
          videoId={directVideoSharingEnabled ? id : undefined}
          shareSectionId={directVideoSharingEnabled ? id : undefined}
          showCaption={true}
          title={embedDisplayTitle}
          thumbnail={displayEmbedThumbnail}
          overlayDescriptionHtml={overlayDescriptionHtml}
          suppressDirectVideoSharingFeatures={suppressLegacyDirectVideoSharing}
          playerOptions={{
            muted: isMuted,
          }}
          aspectRatio="wide"
        />
      )}
    </VideoContainer>
  );
}

export const VIDEO_PRIMITIVE_DISPLAY_NAME = "Video";

Video.craft = {
  related: {
    settings: VideoSettings,
    leftOutlineOptions: PrimitivesFloatingOptions,
  },
  custom: {
    displayName: VIDEO_PRIMITIVE_DISPLAY_NAME,
    settingsTitle: VIDEO_PRIMITIVE_DISPLAY_NAME,
  },
};
